import PropTypes from "prop-types";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import styled, { css } from "styled-components";

function ProductsList({ plans, selectedPlanId, onSelectPlan }) {
  return (
    <StyledGrid>
      {plans.map((plan) => {
        const isSelected = plan.tierId === selectedPlanId;

        return (
          <PlanCard
            key={plan.tierId}
            $selected={isSelected}
            $highlight={plan.highlight}
            onClick={() => onSelectPlan(plan.tierId)}
            type="button"
          >
            {plan.badgeText && (
              <Badge>
                <WorkspacePremiumRoundedIcon fontSize="inherit" />
                {plan.badgeText}
              </Badge>
            )}

            <Header>
              <TitleRow>
                <div>
                  <PlanName>{plan.name}</PlanName>
                  <Headline>{plan.headline}</Headline>
                </div>
                {isSelected && (
                  <SelectedMark>
                    <CheckRoundedIcon fontSize="inherit" />
                  </SelectedMark>
                )}
              </TitleRow>

              <PriceRow>
                <Price>{plan.formattedPrice}</Price>
                <BillingLabel>{plan.billingLabel}</BillingLabel>
              </PriceRow>

              <Description>{plan.description}</Description>
            </Header>

            <FeaturesList>
              {plan.features.map((feature) => (
                <FeatureItem key={feature}>
                  <FeatureIcon>
                    <CheckRoundedIcon fontSize="inherit" />
                  </FeatureIcon>
                  <span>{feature}</span>
                </FeatureItem>
              ))}
            </FeaturesList>

            <Footer>
              <SelectLabel $selected={isSelected}>
                {isSelected ? "Plano selecionado" : plan.ctaLabel}
                <ArrowForwardRoundedIcon fontSize="inherit" />
              </SelectLabel>
              <RenewalHint>{plan.renewalLabel}</RenewalHint>
            </Footer>
          </PlanCard>
        );
      })}
    </StyledGrid>
  );
}

ProductsList.propTypes = {
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      badgeText: PropTypes.string,
      billingLabel: PropTypes.string.isRequired,
      ctaLabel: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      features: PropTypes.arrayOf(PropTypes.string).isRequired,
      formattedPrice: PropTypes.string.isRequired,
      headline: PropTypes.string.isRequired,
      highlight: PropTypes.bool,
      name: PropTypes.string.isRequired,
      renewalLabel: PropTypes.string.isRequired,
      tierId: PropTypes.number.isRequired,
    })
  ).isRequired,
  selectedPlanId: PropTypes.number,
  onSelectPlan: PropTypes.func.isRequired,
};

ProductsList.defaultProps = {
  selectedPlanId: null,
};

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.25rem;
`;

const selectedCardStyles = css`
  transform: translateY(-2px);
  border-color: rgba(15, 118, 110, 0.55);
  box-shadow: 0 28px 60px rgba(15, 23, 42, 0.14);
`;

const highlightedCardStyles = css`
  background:
    linear-gradient(180deg, rgba(255, 248, 235, 0.98), rgba(255, 255, 255, 0.98)),
    #ffffff;
`;

const PlanCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-height: 100%;
  padding: 1.5rem;
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 28px;
  text-align: left;
  background:
    linear-gradient(180deg, rgba(244, 248, 252, 0.96), rgba(255, 255, 255, 0.98)),
    #ffffff;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
  cursor: pointer;
  transition:
    transform 160ms ease,
    box-shadow 160ms ease,
    border-color 160ms ease;

  &:hover {
    ${selectedCardStyles}
  }

  ${({ $selected }) => $selected && selectedCardStyles}
  ${({ $highlight }) => $highlight && highlightedCardStyles}
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  width: fit-content;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  background: rgba(180, 83, 9, 0.12);
  color: #9a3412;
  font-size: 0.82rem;
  font-weight: 700;
`;

const Header = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const PlanName = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  color: #0f172a;
`;

const Headline = styled.p`
  margin: 0.35rem 0 0;
  color: #0f766e;
  font-size: 0.96rem;
  font-weight: 700;
`;

const SelectedMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  background: #0f766e;
  color: #ffffff;
  font-size: 1rem;
  flex-shrink: 0;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Price = styled.span`
  font-size: 2.2rem;
  font-weight: 800;
  color: #111827;
`;

const BillingLabel = styled.span`
  color: #475569;
  font-size: 0.95rem;
  font-weight: 600;
`;

const Description = styled.p`
  margin: 0;
  color: #475569;
  line-height: 1.6;
`;

const FeaturesList = styled.ul`
  display: grid;
  gap: 0.75rem;
  padding: 0;
  margin: 0;
  list-style: none;
`;

const FeatureItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.7rem;
  align-items: flex-start;
  color: #0f172a;
  line-height: 1.5;
`;

const FeatureIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.4rem;
  height: 1.4rem;
  margin-top: 0.1rem;
  border-radius: 999px;
  background: rgba(15, 118, 110, 0.14);
  color: #0f766e;
  font-size: 0.9rem;
`;

const Footer = styled.div`
  display: grid;
  gap: 0.45rem;
  margin-top: auto;
`;

const SelectLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: ${({ $selected }) => ($selected ? "#0f766e" : "#111827")};
  font-weight: 800;
`;

const RenewalHint = styled.span`
  color: #64748b;
  font-size: 0.9rem;
`;

export default ProductsList;
